import { useState } from 'react';
import { TextInput, useRecordContext } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { Box, Button, Collapse, Stack, TextField as MTextField, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useLoadGoogleMaps } from '../hooks/useLoadGoogleMaps';

const FIELDS = [
  'pickup_address.adresse',
  'pickup_address.municipalite',
  'pickup_address.province',
  'pickup_address.codePostal',
  'pickup_address.pays',
  'pickup_address.lat',
  'pickup_address.lng',
  'pickup_address.geocoded_from',
];

/**
 * Where the body is collected, when that is not the place of death.
 *
 * An exception, not a second address to maintain: left empty, the transfer is
 * assumed to start at the place of death, which is what happens on almost every
 * case. Filling it in is a deliberate act, so it is behind a toggle rather than
 * sitting open inviting entry.
 *
 * It matters because the out-of-zone transport charge is measured from here —
 * an address entered by habit rather than because collection actually moved
 * would quietly change what a family is billed.
 */
export const FuneralPickupAddressInput = () => {
  const record = useRecordContext();
  const { setValue } = useFormContext();
  const { isLoaded: mapsLoaded } = useLoadGoogleMaps(
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  );

  const street = useWatch({ name: 'pickup_address.adresse' });
  const city = useWatch({ name: 'pickup_address.municipalite' });
  const hasOverride = Boolean((street ?? '').trim() || (city ?? '').trim());

  const [expanded, setExpanded] = useState(hasOverride);
  const [addressValue, setAddressValue] = useState<any>(null);

  const handlePlaceChange = (selected: any) => {
    setAddressValue(selected);
    if (!selected?.value?.place_id || !window.google?.maps?.places) return;

    const service = new window.google.maps.places.PlacesService(
      document.createElement('div'),
    );
    service.getDetails(
      { placeId: selected.value.place_id, fields: ['address_components', 'geometry'] },
      (place: any, status: any) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;

        let streetNumber = '', route = '', municipality = '', province = '', postal = '', country = '';
        place.address_components?.forEach((c: any) => {
          if (c.types.includes('street_number')) streetNumber = c.long_name;
          else if (c.types.includes('route')) route = c.long_name;
          else if (c.types.includes('locality') || c.types.includes('sublocality_level_1')) municipality = c.long_name;
          else if (c.types.includes('administrative_area_level_1')) province = c.short_name;
          else if (c.types.includes('postal_code')) postal = c.long_name;
          else if (c.types.includes('country')) country = c.short_name;
        });

        setValue('pickup_address.adresse', [streetNumber, route].filter(Boolean).join(' '), { shouldDirty: true });
        setValue('pickup_address.municipalite', municipality, { shouldDirty: true });
        setValue('pickup_address.province', province, { shouldDirty: true });
        setValue('pickup_address.codePostal', postal, { shouldDirty: true });
        setValue('pickup_address.pays', country || 'Canada', { shouldDirty: true });

        // Captured here so the zone test never has to geocode this case.
        const loc = place.geometry?.location;
        if (loc) {
          setValue('pickup_address.lat', loc.lat(), { shouldDirty: true });
          setValue('pickup_address.lng', loc.lng(), { shouldDirty: true });
          setValue(
            'pickup_address.geocoded_from',
            [[streetNumber, route].filter(Boolean).join(' '), municipality, province, postal]
              .map((x) => (x ?? '').trim())
              .filter(Boolean)
              .join(', '),
            { shouldDirty: true },
          );
        }
      },
    );
  };

  const clearOverride = () => {
    // Null, not blank strings: the transport calculation treats an empty
    // pickup_address as "use the place of death", and a row of empty strings
    // would read as a deliberate — but unusable — override.
    FIELDS.forEach((f) => setValue(f, null, { shouldDirty: true }));
    setAddressValue(null);
    setExpanded(false);
  };

  if (!record) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Button
        size="small"
        onClick={() => setExpanded(!expanded)}
        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ textTransform: 'none', color: hasOverride ? 'warning.main' : 'text.secondary' }}
      >
        {hasOverride ? 'Collected elsewhere — pickup address set' : 'Body collected somewhere else?'}
      </Button>

      <Collapse in={expanded}>
        <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid #e0e0e0' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Leave empty when the transfer starts at the place of death — that is the
            normal case. Set this only when the body is collected somewhere else; the
            out-of-zone transport charge is measured from whatever is here.
          </Typography>

          <Box sx={{ width: 400, mb: 1 }}>
            {mapsLoaded ? (
              <GooglePlacesAutocomplete
                selectProps={{
                  value: addressValue,
                  onChange: handlePlaceChange,
                  placeholder: 'Search for the pickup address…',
                  isClearable: true,
                  styles: { menu: (p: any) => ({ ...p, zIndex: 9999 }) },
                }}
                autocompletionRequest={{ componentRestrictions: { country: ['ca'] } }}
              />
            ) : (
              <MTextField disabled fullWidth label="Loading address search…" />
            )}
          </Box>

          <TextInput source="pickup_address.adresse" label="Street Address" fullWidth helperText={false} />
          <TextInput source="pickup_address.municipalite" label="City / Municipality" fullWidth helperText={false} />
          <TextInput source="pickup_address.province" label="Province" fullWidth helperText={false} />
          <TextInput source="pickup_address.codePostal" label="Postal Code" fullWidth helperText={false} />

          {hasOverride && (
            <Stack direction="row" sx={{ mt: 1 }}>
              <Button size="small" color="warning" onClick={clearOverride}>
                Clear — collect at the place of death
              </Button>
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default FuneralPickupAddressInput;
